import jsPDF from 'jspdf';
// Use html2canvas-pro (drop-in fork of html2canvas) so modern color formats
// like oklch() — emitted by Tailwind v4's default palette — capture correctly.
// The original html2canvas@1.4.1 silently fails on any element using oklch.
import html2canvas from 'html2canvas-pro';

export interface ExportPdfOptions {
  filename: string;
  /** Page margin in mm. Defaults to 10. */
  margin?: number;
  /** Override the default footer line; falls back to "Generated <local timestamp>". */
  footer?: string;
  /**
   * html2canvas pixel density multiplier. Higher = sharper but bigger PDFs.
   * 1.5 keeps text crisp on A4 print while staying well under email size limits.
   * Default 1.5.
   */
  scale?: number;
  /**
   * JPEG quality 0–1 used when encoding each page slice. 0.82 is visually
   * indistinguishable from PNG for UI screenshots and shrinks the file ~15-30x.
   * Default 0.82.
   */
  jpegQuality?: number;
}

/**
 * Capture a DOM element with html2canvas and write it to a paginated A4 PDF.
 *
 * Implementation notes (size matters here — earlier versions emitted ~400 MB
 * PDFs because they embedded the full-height capture once per page as PNG):
 *   - We slice the source canvas into one sub-canvas per page and encode each
 *     slice independently, so each page's image only carries its own pixels.
 *   - Slices are encoded as JPEG; PNG is lossless but ~15-30x larger and not
 *     useful for chart/text screenshots.
 *   - Default scale is 1.5 (crisp at A4 print without quadrupling pixel counts
 *     the way scale 2 does).
 *
 * Always renders against a white background so dark-mode users get a
 * readable PDF rather than a low-contrast one.
 *
 * Any descendant with `data-pdf-expand` will have its scroll constraints
 * cleared in the cloned DOM before capture, so long tables that are
 * scrollable on screen render their full content in the PDF.
 */
export async function exportElementToPdf(
  element: HTMLElement,
  {
    filename,
    margin = 10,
    footer,
    scale = 1.5,
    jpegQuality = 0.82,
  }: ExportPdfOptions,
): Promise<void> {
  const sourceCanvas = await html2canvas(element, {
    scale,
    useCORS: true,
    logging: false,
    backgroundColor: '#ffffff',
    onclone: (_doc, clonedElement) => {
      const expandable = clonedElement.querySelectorAll<HTMLElement>('[data-pdf-expand]');
      expandable.forEach((node) => {
        node.style.maxHeight = 'none';
        node.style.height = 'auto';
        node.style.overflow = 'visible';
      });
    },
  });

  const pdf = new jsPDF('p', 'mm', 'a4');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const footerHeight = 8;

  const printableWidth = pageWidth - margin * 2;
  const printableHeight = pageHeight - margin * 2 - footerHeight;

  // mm-per-pixel conversion based on width fit; we use the same ratio for
  // height so the per-page slice height in source-pixels works out evenly.
  const mmPerPx = printableWidth / sourceCanvas.width;
  const pageSlicePxHeight = Math.floor(printableHeight / mmPerPx);

  const totalPages = Math.max(
    1,
    Math.ceil(sourceCanvas.height / pageSlicePxHeight),
  );

  const stamp =
    footer ??
    `Generated ${new Date().toLocaleString('en-ZA', { hour12: false })}`;

  // Reuse one offscreen canvas for every slice — avoids re-allocating a large
  // ArrayBuffer per page on long exports.
  const sliceCanvas = document.createElement('canvas');
  const sliceCtx = sliceCanvas.getContext('2d');
  if (!sliceCtx) {
    throw new Error('Could not get 2d context for PDF slice canvas');
  }

  for (let pageIndex = 0; pageIndex < totalPages; pageIndex += 1) {
    if (pageIndex > 0) pdf.addPage();

    const sliceY = pageIndex * pageSlicePxHeight;
    const sliceHeight = Math.min(
      pageSlicePxHeight,
      sourceCanvas.height - sliceY,
    );

    sliceCanvas.width = sourceCanvas.width;
    sliceCanvas.height = sliceHeight;
    // White fill so transparent pixels don't go black under JPEG (JPEG has no
    // alpha channel — without this, dark-mode captures would render with a
    // solid black background).
    sliceCtx.fillStyle = '#ffffff';
    sliceCtx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
    sliceCtx.drawImage(
      sourceCanvas,
      0,
      sliceY,
      sourceCanvas.width,
      sliceHeight,
      0,
      0,
      sourceCanvas.width,
      sliceHeight,
    );

    const sliceData = sliceCanvas.toDataURL('image/jpeg', jpegQuality);
    const sliceHeightMm = sliceHeight * mmPerPx;
    pdf.addImage(
      sliceData,
      'JPEG',
      margin,
      margin,
      printableWidth,
      sliceHeightMm,
      `slice-${pageIndex}`,
      'FAST',
    );

    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(120);
    pdf.text(stamp, margin, pageHeight - 3);
    pdf.text(
      `Page ${pageIndex + 1} / ${totalPages}`,
      pageWidth - margin,
      pageHeight - 3,
      { align: 'right' },
    );
    pdf.setTextColor(0);
  }

  pdf.save(filename);
}
